import type { Model } from '@/types';

export type ModelSizeStop = {
  value: number;          // 2, 7, 13, 30, 70, 120, 405
  label: string;          // "2B", "7B", etc.
  description: string;    // "Ultra-light", "Medium", etc.
  tolerance: number;      // +/- 10B window for model selection
};

export const MODEL_SIZE_STOPS: ModelSizeStop[] = [
  { value: 2, label: "2B", description: "Entry", tolerance: 10 },
  { value: 7, label: "7B", description: "Compact", tolerance: 10 },
  { value: 15, label: "15B", description: "Balanced", tolerance: 10 },
  { value: 70, label: "70B", description: "Large", tolerance: 10 },
  { value: 120, label: "120B", description: "XL", tolerance: 10 },
  { value: 405, label: "405B", description: "Ultra", tolerance: 10 },
];

// Group models by slider stops
export function groupModelsByStop(models: Model[]): Map<number, Model[]> {
  const grouped = new Map<number, Model[]>();

  for (const model of models) {
    const paramsB = typeof model.params_b === 'number' ? model.params_b : Number.NaN;
    if (!Number.isFinite(paramsB)) {
      continue;
    }

    const stop = findStopForModel(paramsB);
    if (!stop) {
      continue;
    }

    const bucket = grouped.get(stop.value) ?? [];
    bucket.push(model);
    grouped.set(stop.value, bucket);
  }

  for (const [stopValue, entries] of grouped.entries()) {
    entries.sort((a, b) => {
      const diffA = Math.abs((a.params_b ?? 0) - stopValue);
      const diffB = Math.abs((b.params_b ?? 0) - stopValue);
      if (diffA !== diffB) {
        return diffA - diffB;
      }
      const nameA = a.display_name || a.model_id;
      const nameB = b.display_name || b.model_id;
      return nameA.localeCompare(nameB);
    });
  }

  return grouped;
}

// Get default model for a stop
export function getDefaultModelForStop(models: Model[], stopValue: number): Model | null {
  const grouped = groupModelsByStop(models);
  const modelsForStop = grouped.get(stopValue) ?? [];
  return modelsForStop[0] ?? null;
}

// Find which stop a model belongs to
export function findStopForModel(modelParamsB: number): ModelSizeStop | null {
  if (!Number.isFinite(modelParamsB)) {
    return null;
  }

  let closest: ModelSizeStop | null = null;
  let smallestDiff = Number.POSITIVE_INFINITY;

  for (const stop of MODEL_SIZE_STOPS) {
    const diff = Math.abs(modelParamsB - stop.value);
    if (diff <= stop.tolerance && diff < smallestDiff) {
      closest = stop;
      smallestDiff = diff;
    }
  }

  return closest;
}

// Get available stops (those with models)
export function getAvailableStops(models: Model[]): ModelSizeStop[] {
  const grouped = groupModelsByStop(models);
  return MODEL_SIZE_STOPS.filter(stop => grouped.has(stop.value));
}

// Get default model - prefer 70B llama3-70b
export function getDefaultModel(models: Model[]): Model | null {
  if (!models.length) return null;
  
  // First, try to find llama3-70b by model_id
  const llama70b = models.find(m => m.model_id === 'llama3-70b');
  if (llama70b) return llama70b;
  
  // Fallback: find any model close to 70B
  const grouped = groupModelsByStop(models);
  const modelsAt70B = grouped.get(70);
  if (modelsAt70B && modelsAt70B.length > 0) {
    return modelsAt70B[0];
  }
  
  // Final fallback: first model in catalog
  return models[0];
}
